import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

// 菜单状态本地存储的 key
const MENU_COLLAPSED_KEY = 'syhubdocs_menu_collapsed';

// App 嵌入模式的 key
const EMBED_SESSION_KEY = '_app_embed';

/**
 * 检测是否处于 App 嵌入模式
 * 优先检查 localStorage._app_embed（由 Electron webview 注入），
 * 其次检查 URL 参数 _embed=app，再次检查 sessionStorage 缓存
 */
const detectAppEmbed = (): boolean => {
  try {
    if (localStorage.getItem('_app_embed') === '1') {
      sessionStorage.setItem(EMBED_SESSION_KEY, '1');
      return true;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('_embed') === 'app') {
      sessionStorage.setItem(EMBED_SESSION_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(EMBED_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const isAppEmbed = detectAppEmbed();

if (typeof document !== 'undefined' && isAppEmbed) {
  document.body.classList.add('syhub-app-embed');
}

// 不需要登录验证的路径前缀
const publicPathPrefixes = ['/landing', '/user/login', '/share', '/shares', '/app'];

// 检查是否是公开路径的函数
const isPublicPath = (pathname: string): boolean => {
  if (pathname === '/') {
    return true;
  }
  // /docs 精确路径需要登录，但 /docs/:groupSlug 及更深层阅读页保持公开
  if (pathname === '/docs' || pathname === '/docs/') {
    return false;
  }
  if (pathname.startsWith('/docs/')) {
    return true;
  }
  return publicPathPrefixes.some(prefix => pathname.startsWith(prefix));
};

/**
 * 获取菜单收起状态（从 localStorage 读取，默认为收起）
 */
const getMenuCollapsedState = (): boolean => {
  try {
    const stored = localStorage.getItem(MENU_COLLAPSED_KEY);
    if (stored !== null) {
      return stored === 'true';
    }
  } catch (e) {
    console.warn('读取菜单状态失败:', e);
  }
  // 默认收起
  return true;
};

/**
 * 保存菜单收起状态到 localStorage
 */
const saveMenuCollapsedState = (collapsed: boolean): void => {
  try {
    localStorage.setItem(MENU_COLLAPSED_KEY, String(collapsed));
    console.log('✅ 菜单状态已保存:', collapsed ? '收起' : '展开', `(${collapsed})`);
  } catch (e) {
    console.warn('保存菜单状态失败:', e);
  }
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  isAppEmbed?: boolean;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error) {
      // 不在这里跳转，让 onPageChange 来处理未登录用户的跳转逻辑
      // 这样可以确保公开路径（如 landing 页面）不会被强制跳转到登录页
      console.log('获取用户信息失败，用户可能未登录');
    }
    return undefined;
  };

  // 检查是否是公开路径
  const { location } = history;
  
  // 从 localStorage 读取菜单状态
  const menuCollapsed = getMenuCollapsedState();
  console.log('📖 读取到的菜单状态:', menuCollapsed ? '收起' : '展开', `(${menuCollapsed})`);
  
  // 如果是公开路径，不需要获取用户信息
  if (isPublicPath(location.pathname)) {
    return {
      fetchUserInfo,
      isAppEmbed,
      settings: {
        ...defaultSettings,
        collapsed: menuCollapsed,
      } as Partial<LayoutSettings>,
    };
  }
  
  // 如果不是登录页面，执行用户信息获取
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      isAppEmbed,
      settings: {
        ...defaultSettings,
        collapsed: menuCollapsed,
      } as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    isAppEmbed,
    settings: {
      ...defaultSettings,
      collapsed: menuCollapsed,
    } as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  // 标记是否是初始化阶段，避免 ProLayout 的 bug（初始 collapsed=true 会触发 onCollapse(false)）
  let isInitialMount = true;
  
  // 延迟标记，避免初始触发时保存错误状态
  setTimeout(() => {
    isInitialMount = false;
  }, 100);

  const embedMode = initialState?.isAppEmbed;

  return {
    ...initialState?.settings,
    ...(embedMode ? {
      headerRender: false,
      menuRender: false,
      footerRender: false,
    } : {}),
    // 只保留语言切换
    ...(!embedMode ? {
      actionsRender: () => [<SelectLang key="SelectLang" />],
    } : {}),
    avatarProps: embedMode ? false : {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
      markStyle: {
        backgroundColor: 'rgba(244, 246, 249, 0)',
      },
    },
    ...(!embedMode ? { footerRender: () => <Footer /> } : {}),
    onPageChange: () => {
      const { location } = history;
      
      // 如果是公开路径，不检查登录状态
      if (isPublicPath(location.pathname)) {
        return;
      }
      
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    // 监听菜单折叠状态变化，保存到 localStorage
    onCollapse: (collapsed: boolean) => {
      // 忽略初始化阶段的触发（ProLayout 的 bug）
      if (isInitialMount) {
        console.log('⚠️ 忽略初始触发 onCollapse:', collapsed);
        return;
      }
      
      console.log('🔄 onCollapse 触发，新状态:', collapsed ? '收起' : '展开', `(${collapsed})`);
      saveMenuCollapsedState(collapsed);
      
      // 同时更新 initialState 中的设置
      setInitialState({
        ...initialState,
        settings: {
          ...initialState?.settings,
          collapsed,
        },
      });
      console.log('✨ initialState 已更新');
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      return (
        <>
          {children}
        </>
      );
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
  // 默认超时配置
  timeout: 60000, // 1分钟超时
};
